import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, signal, untracked, inject } from '@angular/core';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { StepperComponent } from '../../../../shared/components/stepper/stepper.component';
import { AvatarModule } from 'primeng/avatar';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ActiveStepPipe } from '../ride-item/active-step.pipe';
import { FireStoreService } from '../../../../shared/services/firestore.service';
import { LoaderService } from '../../../../core/components/loader/loader.service';
import { MessageService } from 'primeng/api';
import { ActionDialogService } from '../../services/action-dialog.service';
import { getFormattedDate } from '../../../../shared/utilities/utility-functions';
import { EnvConfigService } from '../../../../shared/services/env-config.service';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { TableModule } from 'primeng/table';
import { TagComponent } from '../../../../shared/components/tag/tag.component';
import { NoRecordsComponent } from '../../../../shared/components/no-records/no-records.component';
import { IStep } from '../../../../shared/interfaces/step.interface';
import { BadgeComponent } from '../../../../shared/components/badge/badge.component';
import { RideDetailsMapProComponent } from '../ride-details-map-pro/ride-details-map-pro.component';
import { UserRole } from '../../../../core/auth/enums/userRoles';
import { RideUpdatesService } from '../../services/ride-updates.service';

@Component({
  selector: 'app-map-dialog',
  standalone: true,
  imports: [ButtonComponent, CommonModule, StepperComponent, ActiveStepPipe, AvatarModule, TableModule, TagComponent, NoRecordsComponent, BadgeComponent, RideDetailsMapProComponent],
  templateUrl: './map-dialog.component.html',
  styleUrl: './map-dialog.component.scss',
})
export class MapDialogComponent implements OnInit, OnDestroy {
  ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);
  private readonly firesStoreService = inject(FireStoreService);
  private readonly loaderService = inject(LoaderService);
  private readonly messageService = inject(MessageService);
  private readonly dialogService = inject(DialogService);
  private readonly actionDialogService = inject(ActionDialogService);
  private readonly env = inject(EnvConfigService);
  private readonly rideUpdatesService = inject(RideUpdatesService);

  mapDialogSub!: Subscription;
  instanceId = crypto.randomUUID();

  stopsTableData = signal<any>([]);
  rideTimelineData = signal<any[]>([]);
  mappedRideData = signal<any>(null);
  columns: any[] = [];

  isAdminRole = localStorage.getItem('role')?.toLowerCase() == UserRole.Admin;

  private rideUpdatesSub?: Subscription;

  ngOnInit() {
    // Set initial data
    this.rideTimelineData.set([...this.config.data.rideTimeline]);
    this.mappedRideData.set(this.config.data.mappedRide);
    this.mapStopsTableData();
    this.setStopsTableColumns();

    // Subscribe to ride updates if rideId is provided
    if (this.config.data.rideId) {
      const updates$ = this.rideUpdatesService.startTrackingRide(this.config.data.rideId);
      this.rideUpdatesSub = updates$.subscribe(updatedRide => {
        // Update local signals with new data using untracked to avoid signal write errors
        if (updatedRide?.rideTimeline) {
          untracked(() => {
            this.rideTimelineData.set([...updatedRide.rideTimeline]);
            this.mappedRideData.set(updatedRide);
            this.mapStopsTableData();
          });
        }
      });
    }
  }

  ngOnDestroy() {
    // Clean up subscription and stop tracking
    this.rideUpdatesSub?.unsubscribe();
    this.rideUpdatesService.stopTracking();
  }

  setStopsTableColumns() {
    this.columns = [
      { field: 'place', name: 'Stop' },
      { field: 'status', name: 'Status' },
      { field: 'scheduledArrivalTime', name: 'Scheduled Time' },
      { field: 'estimatedArrivalTime', name: 'ETA' },
      { field: 'actualArrivalTime', name: 'Actual Time' },
      { field: 'reason', name: 'Reason' },
      { field: 'students', name: 'Students' },
      { field: 'studentsStatus', name: '' },
    ];
  }

  mapStopsTableData() {
    const tableData = this.rideTimelineData().map((stop: IStep) => {
      return {
        place: stop.place,
        status: {
          type: stop.statusType,
          severity: stop.color,
        },
        scheduledArrivalTime: stop.scheduledArrivalTime,
        estimatedArrivalTime: (() => {
          if (stop.arrivalTimeStatus == 'skipped') return 'N/A';
          if (stop.estimatedArrivalTime && stop.estimatedArrivalTime.trim() != '') return stop.estimatedArrivalTime;
          return 'Pending...';
        })(),
        actualArrivalTime: {
          time: (() => {
            if (stop.arrivalTimeStatus == 'skipped') return 'N/A';
            if (stop.actualArrivalTime && stop.actualArrivalTime.trim() != '') return stop.actualArrivalTime;
            return 'Pending...';
          })(),
          description: stop.statusDescription,
          arrivalTimeStatus: stop.arrivalTimeStatus,
        },
        reason: stop.delayReason ?? 'N/A',
        students: stop.cargoes?.map(cargo => {
          return { name: cargo.name, actionType: cargo.actionType, status: cargo.status };
        }),
      };
    });
    this.stopsTableData.set(tableData);
  }

  acknowledgeRideEmergency() {
    this.loaderService.show();
    const date = moment().toDate();
    const formattedDate = getFormattedDate(date);

    this.firesStoreService
      .updateDocField(`${this.env.getConfig().emergenciesCollection}/date_${formattedDate}/alerts`, this.config.data.emergencyDocId, { is_acknowledged: true })
      .then(res => {
        this.loaderService.hide();
        console.log(res);
        this.ref.close();
      })
      .catch(err => {
        this.loaderService.hide();
        console.log(err);
      });
  }

  openBlueprintDialog() {
    this.actionDialogService.openBlueprintDialog(this.dialogService, this.mappedRideData(), this.config.data.docRide);
  }
  openContractorDialog() {
    this.actionDialogService.openContractorDialog(this.dialogService, this.messageService, this.mappedRideData(), this.config.data.docRide);
  }
}
